import { Shelf } from "@/components/mobile/Shelf";
import { createFileRoute, Link, Outlet, useMatches, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { BookOpen, ChevronDown, Lock, Search, Sprout, X } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { isGuestOpenPath, openGuestLock } from "@/lib/guest-access";
import { GuestBanner } from "@/components/guest/GuestBanner";
import { MemberLockedPage } from "@/components/guest/MemberLockedPage";

import { SavedBookmarks } from "@/components/learn/SavedBookmarks";
import { LEARN_CATEGORIES, type StaticCard } from "@/data/learn/categories";
import { LEARN_TOPICS } from "@/data/learn/topics";
import type { LearnTopic } from "@/data/learn/topic-types";
import type { LearnCategory, LearnPathway } from "@/data/learn/topic-types";
import { track } from "@/lib/track";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/learn")({
  head: () => ({
    meta: [
      { title: "Trauma Recovery Library: C-PTSD & Nervous System Guides" },
      {
        name: "description",
        content:
          "Trauma-informed learning hubs covering trauma types, symptoms, relationships, the nervous system, mental health, recovery and healing.",
      },
      { property: "og:title", content: "Learn — Trauma Recovery Library — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Understand, recognise, practise and reflect — plain-English trauma-recovery modules, exercises and worksheets from Recovery Trauma™.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: LearnLayout,
});

type HubCard = {
  title: string;
  desc: string;
  to: string;
  emoji: string;
  badge?: string;
  counts?: string;
  pathways: LearnPathway[];
};

const CATEGORY_PATHWAYS: Record<LearnCategory, LearnPathway[]> = {
  "understanding-trauma": ["trauma-types", "mental-health"],
  "cptsd-responses": ["symptoms-responses"],
  "nervous-system": ["nervous-system"],
  "cbt-dbt": ["mental-health", "recovery-healing"],
  ifs: ["recovery-healing"],
  relationships: ["relationships-abuse"],
  "bonds-boundaries": ["relationships-abuse"],
  "shame-self-worth": ["recovery-healing"],
  "grief-loss": ["trauma-types", "recovery-healing"],
  sleep: ["symptoms-responses"],
  safety: ["recovery-healing"],
};

const PATHWAYS: { key: LearnPathway; label: string }[] = [
  { key: "trauma-types", label: "Trauma Types" },
  { key: "symptoms-responses", label: "Symptoms & Responses" },
  { key: "relationships-abuse", label: "Relationships & Abuse" },
  { key: "nervous-system", label: "Nervous System" },
  { key: "mental-health", label: "Mental Health" },
  { key: "recovery-healing", label: "Recovery & Healing" },
];

function topicToCard(topic: LearnTopic): HubCard {
  const resources = topic.sections.reduce((n, s) => n + s.resources.length, 0);
  return {
    title: topic.title,
    desc: topic.cardBlurb ?? topic.tagline,
    to: `/learn/${topic.slug}`,
    emoji: topic.emoji,
    badge: topic.isNew ? "New" : undefined,
    counts: `${topic.sections.length} sections · ${resources} resources`,
    pathways: topic.pathways ?? (topic.category ? CATEGORY_PATHWAYS[topic.category] : ["trauma-types"]),
  };
}

function staticToCard(card: StaticCard, category: LearnCategory): HubCard {
  return { title: card.title, desc: card.desc, to: card.to, emoji: card.emoji, pathways: CATEGORY_PATHWAYS[category] };
}

const ALL_TOPICS = Object.values(LEARN_TOPICS);

function cardsFor(key: string): HubCard[] {
  const category = LEARN_CATEGORIES.find((c) => c.key === key);
  const topicCards = ALL_TOPICS.filter((t) => t.category === key).map(topicToCard);
  const staticCards = (category?.staticCards ?? []).map((card) => staticToCard(card, category?.key ?? "understanding-trauma"));
  return [...topicCards, ...staticCards];
}

const NEW_CARD_LIMIT = 6;
const NEW_CARDS: HubCard[] = ALL_TOPICS.filter((t) => t.isNew)
  .map(topicToCard)
  .sort((a, b) => {
    if (a.to === "/learn/ptsd") return -1;
    if (b.to === "/learn/ptsd") return 1;
    return 0;
  })
  .slice(0, NEW_CARD_LIMIT);

/** True when a signed-out visitor may not open this Learning Hub card. */
function useCardLocked(to: string) {
  const { loading, isAuthed } = useMembership();
  return !loading && !isAuthed && !isGuestOpenPath(to);
}

function LockOrBadge({ card }: { card: HubCard }) {
  const locked = useCardLocked(card.to);
  if (locked) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border/70 bg-background/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        <Lock className="h-3 w-3" aria-hidden="true" /> Members
      </span>
    );
  }
  if (card.badge) {
    return (
      <span className="shrink-0 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
        {card.badge}
      </span>
    );
  }
  return null;
}

function CardShell({
  card,
  source,
  children,
}: {
  card: HubCard;
  source: string;
  children: React.ReactNode;
}) {
  const locked = useCardLocked(card.to);
  const className =
    "group flex h-full w-full flex-col rounded-2xl border border-border/70 bg-card/70 p-4 text-start shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  if (locked) {
    return (
      <button type="button" onClick={() => openGuestLock(card.title)} className={className}>
        {children}
      </button>
    );
  }
  return (
    <Link
      to={card.to}
      onClick={() =>
        track("learn_card_click", { title: card.title, to: card.to, section: source }, { source: "learn_hub" })
      }
      className={className}
    >
      {children}
    </Link>
  );
}

function CardGrid({ cards, source }: { cards: HubCard[]; source: string }) {
  return (
    <Shelf cols={3} cardWidth="md">
      {cards.map((c) => (
        <li key={c.to}>
          <CardShell card={c} source={source}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-2xl" aria-hidden="true">
                {c.emoji}
              </span>
              <LockOrBadge card={c} />
            </div>
            <h3 className="mt-2 font-serif text-lg leading-snug text-foreground">{c.title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-foreground/70">{c.desc}</p>
            {c.counts ? (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-muted-foreground">{c.counts}</p>
            ) : null}
            <span className="mt-3 inline-block text-sm font-semibold text-foreground/80 underline-offset-4 group-hover:underline">
              Open →
            </span>
          </CardShell>
        </li>
      ))}
    </Shelf>
  );
}

function CategoryBlock({
  id,
  emoji,
  label,
  blurb,
  cards,
  defaultOpen,
}: {
  id: string;
  emoji: string;
  label: string;
  blurb: string;
  cards: HubCard[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  if (!cards.length) return null;

  return (
    <section className="rounded-2xl border border-border/70 bg-background/40">
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`panel-${id}`}
          onClick={() => {
            setOpen((v) => !v);
            if (!open) track("learn_category_open", { category: id }, { source: "learn_hub" });
          }}
          className="flex min-h-14 w-full items-center gap-3 rounded-2xl px-4 py-4 text-start transition-colors hover:bg-card/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="text-2xl" aria-hidden="true">
            {emoji}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-serif text-xl text-foreground">{label}</span>
            <span className="mt-0.5 block text-sm leading-snug text-muted-foreground">{blurb}</span>
          </span>
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
            {cards.length}
          </span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
      </h2>
      {open && (
        <div id={`panel-${id}`} className="px-4 pb-5">
          <CardGrid cards={cards} source={id} />
        </div>
      )}
    </section>
  );
}

const ALL_CARDS: HubCard[] = LEARN_CATEGORIES.flatMap((c) => cardsFor(c.key));

function makeAzGroups(cards: HubCard[]): { letter: string; cards: HubCard[] }[] {
  const seen = new Set<string>();
  const unique = cards.filter((c) => (seen.has(c.to) ? false : (seen.add(c.to), true)));
  const map = new Map<string, HubCard[]>();
  for (const card of unique) {
    const first = card.title.replace(/^[^a-z0-9]+/i, "").charAt(0).toUpperCase();
    const letter = /[A-Z]/.test(first) ? first : "#";
    map.set(letter, [...(map.get(letter) ?? []), card]);
  }
  return [...map.entries()]
    .sort((a, b) => (a[0] === "#" ? 1 : b[0] === "#" ? -1 : a[0].localeCompare(b[0])))
    .map(([letter, cards]) => ({
      letter,
      cards: cards.sort((a, b) => a.title.localeCompare(b.title)),
    }));
}

function AzRow({ card }: { card: HubCard }) {
  const locked = useCardLocked(card.to);
  const className =
    "group flex w-full items-center gap-2 rounded-lg px-2 py-2 text-start text-sm text-foreground/85 transition hover:bg-card/70 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const inner = (
    <>
      <span aria-hidden="true" className="text-base">
        {card.emoji}
      </span>
      <span className="min-w-0 flex-1 truncate">{card.title}</span>
      {locked ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Lock className="h-3 w-3" aria-hidden="true" /> Members
        </span>
      ) : card.badge ? (
        <span className="shrink-0 rounded-full bg-gold/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">
          {card.badge}
        </span>
      ) : null}
    </>
  );
  if (locked) {
    return (
      <button type="button" onClick={() => openGuestLock(card.title)} className={className}>
        {inner}
      </button>
    );
  }
  return (
    <Link
      to={card.to}
      onClick={() =>
        track("learn_card_click", { title: card.title, to: card.to, section: "a-z" }, { source: "learn_hub" })
      }
      className={className}
    >
      {inner}
    </Link>
  );
}

function AtoZIndex({ cards }: { cards: HubCard[] }) {
  const groups = makeAzGroups(cards);
  const [jumpOpen, setJumpOpen] = useState(false);
  const jumpTo = (letter: string) => {
    const el = document.getElementById(`az-${letter}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setJumpOpen(false);
  };
  return (
    <section aria-labelledby="learn-az" className="space-y-6">
      <h2 id="learn-az" className="sr-only">
        All modules A to Z
      </h2>

      {/* Compact "Jump to letter" control — replaces the old 26-button row,
          which forced horizontal scrolling and clipped letters on phones. */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setJumpOpen((v) => !v)}
          aria-expanded={jumpOpen}
          aria-controls="az-jump-panel"
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-gold/50 bg-card/70 px-4 text-sm font-semibold text-foreground transition hover:border-gold hover:bg-gold/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Jump to letter
          <ChevronDown
            className={`h-4 w-4 text-gold transition-transform ${jumpOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>
        {jumpOpen ? (
          <div
            id="az-jump-panel"
            className="absolute start-0 end-0 z-30 mt-2 rounded-2xl border border-gold/40 bg-card p-3 shadow-lg sm:end-auto sm:w-[22rem]"
          >
            <div
              role="group"
              aria-label="Jump to letter"
              className="grid grid-cols-6 gap-1.5 sm:grid-cols-8"
            >
              {groups.map((g) => (
                <button
                  key={g.letter}
                  type="button"
                  onClick={() => jumpTo(g.letter)}
                  aria-label={`Jump to ${g.letter === "#" ? "other" : g.letter}`}
                  className="flex min-h-10 items-center justify-center rounded-lg border border-border/70 bg-background/60 text-sm font-semibold text-foreground/85 transition hover:border-gold/60 hover:bg-gold/10 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {g.letter}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>



      {groups.map((g) => (
        <div key={g.letter} id={`az-${g.letter}`} className="scroll-mt-24">
          <div className="mb-2 flex items-center gap-3">
            <span className="font-serif text-xl text-gold">{g.letter}</span>
            <span className="h-px flex-1 bg-border/70" />
          </div>
          <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
            {g.cards.map((c) => (
              <li key={c.to}>
                <AzRow card={c} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function LearnLayout() {
  const matches = useMatches();
  const lastId = matches[matches.length - 1]?.routeId;
  const isHub = lastId === "/learn" || lastId === "/learn/";
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { loading, isAuthed } = useMembership();
  const isGuest = !loading && !isAuthed;
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"az" | "themes">("az");
  const [pathway, setPathway] = useState<"all" | LearnPathway>("all");

  if (!isHub) {
    if (isGuest && !isGuestOpenPath(pathname)) return <MemberLockedPage />;
    return <Outlet />;
  }

  const q = query.trim().toLowerCase();
  const pathwayCards = pathway === "all" ? ALL_CARDS : ALL_CARDS.filter((card) => card.pathways.includes(pathway));
  const results = q
    ? pathwayCards.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.desc.toLowerCase().includes(q),
      )
    : [];


  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      {isGuest ? <GuestBanner /> : null}
      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" /> Learn
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Understand what your body already knows
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          A structured trauma-recovery library from Recovery Trauma™. Every module follows the
          same rhythm — understand, recognise, practise, reflect — in plain English. Educational
          only, and never a substitute for therapy or diagnosis.
        </p>
        <ol className="mx-auto mt-5 flex max-w-xl flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {["Understand", "Recognise", "Practise", "Reflect"].map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              {i > 0 ? <span aria-hidden="true" className="text-gold">→</span> : null}
              <span className="rounded-full border border-border bg-card px-2.5 py-1">{s}</span>
            </li>
          ))}
        </ol>
      </header>


      <div className="mx-auto mb-8 max-w-2xl">
        <label htmlFor="learn-search" className="sr-only">
          Search the Learning Hub
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id="learn-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search topics — boundaries, flashbacks, sleep…"
            className="min-h-12 w-full rounded-full border border-border bg-card/70 ps-11 pe-11 text-sm text-foreground shadow-sm outline-none transition focus-visible:border-gold/60 focus-visible:ring-2 focus-visible:ring-ring"
          />
          {q ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute end-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      <nav aria-label="Filter learning topics" className="mb-7">
        <p className="mb-2 text-center text-xs font-semibold uppercase text-muted-foreground">Explore a pathway</p>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden">
          <Button
            type="button"
            size="sm"
            variant={pathway === "all" ? "default" : "outline"}
            aria-pressed={pathway === "all"}
            onClick={() => setPathway("all")}
            className="shrink-0 rounded-full"
          >
            All topics
          </Button>
          {PATHWAYS.map((item) => (
            <Button
              key={item.key}
              type="button"
              size="sm"
              variant={pathway === item.key ? "default" : "outline"}
              aria-pressed={pathway === item.key}
              onClick={() => {
                setPathway(item.key);
                setView("az");
                track("learn_pathway_filter", { pathway: item.key }, { source: "learn_hub" });
              }}
              className="shrink-0 rounded-full"
            >
              {item.label}
            </Button>
          ))}
        </div>
      </nav>

      <Link
        to="/learn/therapies"
        onClick={() =>
          track("learn_card_click", { title: "Therapies", to: "/learn/therapies", section: "feature" }, { source: "learn_hub" })
        }
        className="group mb-8 flex flex-col gap-2 rounded-2xl border border-gold/50 bg-gold/10 p-5 transition hover:border-gold hover:bg-gold/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-row sm:items-center sm:gap-5"
      >
        <span aria-hidden="true" className="text-3xl">
          🤝
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-serif text-xl text-foreground">
            Therapies &amp; Healing Approaches
          </span>
          <span className="mt-1 block text-sm leading-relaxed text-foreground/75">
            EMDR, TF-CBT, CPT, Somatic Experiencing, Prolonged Exposure, ACT, Schema Therapy, NET,
            CFT, psychodynamic, IPT — plus CBT, DBT and IFS. What each involves, and how strong the
            evidence really is.
          </span>
        </span>
        <span className="shrink-0 text-sm font-semibold text-foreground/80 underline-offset-4 group-hover:underline">
          Browse therapies →
        </span>
      </Link>



      {q ? (
        <section className="mb-8" aria-live="polite">
          <p className="mb-3 text-sm text-muted-foreground">
            {results.length === 0
              ? "No modules match that search yet — try a different word."
              : `${results.length} ${results.length === 1 ? "result" : "results"} for “${query.trim()}”`}
          </p>
          {results.length > 0 ? <CardGrid cards={results} source="search" /> : null}
        </section>
      ) : (
        <>
          <div className="mb-6 flex justify-center">
            <div
              role="tablist"
              aria-label="Browse the Learning Hub"
              className="inline-flex rounded-full border border-border bg-card/70 p-1"
            >
              {([
                { key: "az", label: "A–Z" },
                { key: "themes", label: "Browse by theme" },
              ] as const).map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={view === t.key}
                  onClick={() => {
                    setView(t.key);
                    track("learn_view_change", { view: t.key }, { source: "learn_hub" });
                  }}
                  className={`min-h-11 rounded-full px-4 text-sm font-semibold transition ${
                    view === t.key
                      ? "bg-gold/20 text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {view === "az" ? (
             <AtoZIndex cards={pathwayCards} />
          ) : (
            <div className="space-y-4">
              {LEARN_CATEGORIES.map((category) => (
                <CategoryBlock
                  key={category.key}
                  id={category.key}
                  emoji={category.emoji}
                  label={category.label}
                  blurb={category.blurb}
                   cards={cardsFor(category.key).filter((card) => pathway === "all" || card.pathways.includes(pathway))}
                  defaultOpen
                />
              ))}
            </div>
          )}

          {NEW_CARDS.length > 0 && (
            <section className="mt-10" aria-labelledby="new-in-learn">
              <div className="mb-3 flex items-center gap-2">
                <Sprout className="h-5 w-5 text-gold" aria-hidden="true" />
                <h2 id="new-in-learn" className="font-serif text-2xl text-foreground">
                  New in Learn 🌱
                </h2>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                The newest learning areas — each one grows over time with guides, exercises and worksheets.
              </p>
              <CardGrid cards={NEW_CARDS} source="new" />
            </section>
          )}
        </>
      )}


      <SavedBookmarks />


      <p className="mt-10 rounded-2xl border border-border bg-muted/30 p-5 text-center text-xs leading-relaxed text-muted-foreground">
        Recovery Trauma™ is a wellbeing and educational platform. It is not a
        crisis service, therapy, diagnosis, or medical treatment. Nothing here can tell you
        whether you have a particular condition — only a qualified professional can do that. If you need
        urgent support, please visit our{" "}
        <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">
          Crisis Resources
        </Link>{" "}
        page.
      </p>
    </main>
  );
}
